/**
 * AdminConnectionPanel React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2019 contexagon GmbH
 */


// React
import React from 'react';
import connect from "react-redux/es/connect/connect";

// Material UI
import { withStyles } from '@material-ui/core/styles';

// Material UI components
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from "@material-ui/core/Tooltip";

// Material UI icons
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import HelpIcon from '@material-ui/icons/Help';
import RefreshIcon from '@material-ui/icons/Refresh';
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled';
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline';
import PausePresentationIcon from '@material-ui/icons/PausePresentation';

// application components
import WarningPanel from "../base/WarningPanel";

// application
import {fetchJSON} from "../../operations/communication";
import {
    REST_CALL_UPDATE_SERVER_CONNECTIONS,
    REST_SERVER_BASE_URL
} from "../../config";
import {toUserFriendlySecondsString} from "../../utils";


function isRunning(connection) {
    if (connection.type === 'sensor') {
        // (some) sensors are not using socket.io, which means they are always "running" when connected
        return connection.connected;
    }
    else {
        // other devices are using socket.io, which means they are "running" if at least
        // one socket.io channel is connected (otherwise they are "halted")
        return connection.connected && connection.channels && connection.channels.length > 0
    }
}


/**
 * Panel for admin page to show active connections fetched from server
 *
 * @author Jens Gruschel
 */
class AdminConnectionPanel extends React.Component {

    state = {
        disconnected: false,
        serverTime: undefined,
        boxConnections: [],
        sensorConnections: [],
        mobileConnections: []
    };

    componentDidMount() {
        this.refreshData();
        this.timer = setInterval(() => {this.refreshData()}, 5000);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    refreshData() {
        this.refreshConnections();
    }

    async refreshConnections() {

        const result = await this.fetchServerConnections();

        if (result) {

            const serverTime = result.time;
            const connections = result.connections || [];
            const boxConnections = connections.filter((connection) => connection.type === 'box' && connection.alive_time >= serverTime - 600);
            const sensorConnections = connections.filter((connection) => connection.type === 'sensor' && connection.alive_time >= serverTime - 600);
            const mobileConnections = connections.filter((connection) => connection.type === 'mobile' && connection.alive_time >= serverTime - 600);

            this.setState({
                disconnected: false,
                serverTime: serverTime,
                boxConnections: boxConnections.sort((a, b) => a.name.localeCompare(b.name) || b.connect_time - a.connect_time),
                sensorConnections: sensorConnections.sort((a, b) => a.name.localeCompare(b.name) || b.connect_time - a.connect_time),
                mobileConnections: mobileConnections.sort((a, b) => b.connect_time - a.connect_time)
            });
        }

        else {
            this.setState({
                disconnected: true
            });
        }
    }

    render() {

        const { classes } = this.props;

        return <div className={classes.panel}>

            {
                this.state.disconnected && <WarningPanel text="Verbindung zum Server unterbrochen!" />
            }

            <ExpansionPanel defaultExpanded={true}>
                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography>CBoxes</Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    {this.renderConnectionTable(this.state.boxConnections)}
                </ExpansionPanelDetails>
            </ExpansionPanel>

            <ExpansionPanel defaultExpanded={true}>
                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography>Sensoren</Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    {this.renderConnectionTable(this.state.sensorConnections)}
                </ExpansionPanelDetails>?page=admin
            </ExpansionPanel>

            <ExpansionPanel defaultExpanded={true}>
                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography>Mobiles</Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    {this.renderConnectionTable(this.state.mobileConnections)}
                </ExpansionPanelDetails>
            </ExpansionPanel>
        </div>;
    }

    renderConnectionTable(connections) {

        const { classes } = this.props;

        return <Table className={classes.table}>

            <TableHead>
                <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Zeit</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Adresse</TableCell>
                    <TableCell>Aktivität</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                </TableRow>
            </TableHead>

            <TableBody>
                {connections.map(connection => {
                    const info = this.getConnectionInfo(connection);
                    const env = connection.env || {};
                    const running = isRunning(connection);
                    return <TableRow key={connection.sid}>
                        <TableCell><Tooltip title={info.state}>{info.icon}</Tooltip></TableCell>
                        <TableCell align="right">{toUserFriendlySecondsString(Math.floor(info.secs))}</TableCell>
                        <TableCell><Tooltip title={"Session " + connection.sid}><span>{connection.name}</span></Tooltip></TableCell>
                        <TableCell><Tooltip title={env['HTTP_USER_AGENT']}><span>{env['REMOTE_ADDR']}</span></Tooltip></TableCell>
                        <TableCell>{connection.activity}</TableCell>
                        <TableCell className={classes.rowActionCell} align="right">
                            {connection.type === 'box' && running && <Tooltip title="anhalten">
                                <IconButton disabled={info.secs < 10}
                                    onClick={() => this.haltBox(connection.sid)}
                                >
                                    <PausePresentationIcon/>
                                </IconButton>
                            </Tooltip>}
                            {connection.type === 'box' && connection.connected && <Tooltip title="neu laden">
                                <IconButton disabled={info.secs < 10}
                                    onClick={() => this.reloadBox(connection.sid)}
                                >
                                    <RefreshIcon/>
                                </IconButton>
                            </Tooltip>}
                        </TableCell>
                    </TableRow>;
                })}
            </TableBody>

        </Table>;
    }

    getConnectionInfo(connection) {
        const now = this.state.serverTime;
        const disconnectTime = connection.disconnect_time || connection.alive_time;
        if (connection.connected) {
            const unaliveSecs = now - connection.alive_time;
            if (unaliveSecs < 10) {
                const running = isRunning(connection);
                const active = connection.active;
                return {
                    state: running ? (active == null ? "online / unknown" : active ? "online" : "online / inactive") : (active == null ? "halted / unknown" : active ? "halted" : "halted / inactive"),
                    icon: running ? (
                        active == null || active ? <CheckCircleIcon color="primary"/> : <CheckCircleOutlineIcon color="primary"/>
                    ) : (
                        active == null || active ? <PauseCircleFilledIcon color="disabled"/> : <PauseCircleOutlineIcon color="disabled"/>
                    ),
                    secs: now - connection.connect_time
                }
            }
            else if (unaliveSecs < 30000) {
                return {
                    state: "unknown",
                    icon: <HelpIcon color="error"/>,
                    secs: now - disconnectTime
                }
            }
            else {
                return {
                    state: "missing",
                    icon: <RemoveCircleIcon color="disabled"/>,
                    secs: now - disconnectTime
                }
            }
        }
        else {
            return {
                state: "disconnected",
                icon: <RemoveCircleIcon color="disabled"/>,
                secs: now - disconnectTime
            }
        }
    }

    async fetchServerConnections() {
        try {
            const result = await fetchJSON(
                REST_SERVER_BASE_URL + REST_CALL_UPDATE_SERVER_CONNECTIONS.path, {
                    method: REST_CALL_UPDATE_SERVER_CONNECTIONS.method,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            return result;
        }
        catch (error) {
            return null;
        }
    }

    async reloadBox(sid) {
        try {
            await fetchJSON(
                REST_SERVER_BASE_URL + "/box/reload?sid=" + sid, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            this.refreshData();
            return true;
        }
        catch (error) {
            return false;
        }
    }

    async haltBox(sid) {
        try {
            await fetchJSON(
                REST_SERVER_BASE_URL + "/box/halt/" + sid, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            this.refreshData();
            return true;
        }
        catch (error) {
            return false;
        }
    }
}


const styles = theme => ({
    panel: {
        width: '100%'
    }
});

const mapStateToProps = state => {
    return {
        accessToken: state.authorization.accessToken
    };
};

export default connect(mapStateToProps)(withStyles(styles)(AdminConnectionPanel));